import { useEffect, useState } from 'react';
import { instituteNow } from '../utils';
import { examEndsAt } from './status';
import type { Exam } from '@/types/exam';

/**
 * Longest single wait. A paper set weeks ahead would otherwise ask for a
 * timeout past what `setTimeout` can hold (~24.8 days, beyond which it fires at
 * once), and a laptop that slept through the moment wakes to a stale timer —
 * waking at least hourly re-reads the clock either way.
 */
const MAX_WAIT_MS = 60 * 60 * 1000;

/** `YYYY-MM-DD HH:MM` in IST → the instant it names. India keeps no daylight saving. */
function toInstant(moment: string): number {
  return new Date(`${moment.replace(' ', 'T')}:00+05:30`).getTime();
}

/**
 * The institute's clock, re-read the moment the next of these papers ends.
 *
 * A paper dated today stays "Scheduled" until its end time; a teacher who left
 * the list open from lunch should see it move to "Marks pending" at 16:30
 * without reloading. One timer aimed at the next end rather than a per-minute
 * tick: nothing else on these screens depends on the time, so re-rendering
 * every minute would buy nothing.
 */
export function useExamClock(
  papers: readonly Pick<Exam, 'examDate' | 'startTime' | 'endTime'>[],
): string {
  const [now, setNow] = useState(instituteNow);

  // Keyed on the next end, not the array, so a refetch returning the same
  // papers does not tear the timer down and set it again.
  const nextEnd = papers
    .map(examEndsAt)
    .filter((end) => end > now)
    .reduce<string | null>((soonest, end) => (soonest === null || end < soonest ? end : soonest), null);

  useEffect(() => {
    if (nextEnd === null) return;
    // A second past the minute, so the re-read lands on or after the end.
    const wait = Math.min(Math.max(toInstant(nextEnd) - Date.now() + 1000, 0), MAX_WAIT_MS);
    const timer = window.setTimeout(() => setNow(instituteNow()), wait);

    return () => window.clearTimeout(timer);
    // `now` as well: after an hourly wake the next end is unchanged, and the
    // timer still has to be set again.
  }, [nextEnd, now]);

  return now;
}
